import React, { useEffect, useMemo, useState } from 'react';

import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useFindGroups } from '../../groups/Group.hooks';
import { Group } from '../../groups/Group.interface';

function compareIds(id: number, value: number | number[] | undefined): boolean {
  return Array.isArray(value) ? value.includes(id) : id === value;
}

export interface BaseGroupSelectorProps {
  disabled?: boolean;
  filterIds?: number[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
}

export interface GroupSelectorSingleProps extends BaseGroupSelectorProps {
  value?: number;
  multiple?: false;
  onChange?: (group: Group | null) => void;
}

export interface GroupSelectorMultipleProps extends BaseGroupSelectorProps {
  value?: number[];
  multiple: boolean;
  onChange?: (groups: Group[]) => void;
}

export type GroupSelectorProps =
  | GroupSelectorSingleProps
  | GroupSelectorMultipleProps;

const GroupSelector: React.FC<GroupSelectorProps> = (
  props: GroupSelectorProps
) => {
  const { value, disabled, multiple = false, textFieldProps, onChange } = props;
  const [groupOptions, setGroupOptions] = useState<Group[]>([]);
  const { groupList, isLoading, get: findGroups } = useFindGroups();
  const filterIds = (props.filterIds || []).join(',');
  // re-render when value changes
  const key: string | number | undefined = Array.isArray(value)
    ? value.join(', ')
    : value;

  useEffect(() => {
    groupList?.data && setGroupOptions(groupList?.data);
  }, [groupList]);

  useEffect(() => {
    const params: ApiParams = {};
    if (filterIds) {
      params.ids = filterIds;
    }
    findGroups(params);
  }, [filterIds, findGroups]);

  const group: Group | Group[] | null = useMemo(() => {
    const groups: Group[] = groupOptions.filter((option: Group) => {
      return compareIds(option.id, value);
    });
    return multiple ? groups : groups.shift() || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value, groupOptions, multiple]);

  return (
    <Autocomplete
      className='field'
      loading={isLoading}
      value={group}
      options={groupOptions}
      disabled={disabled}
      multiple={multiple}
      getOptionLabel={option => option.name}
      getOptionSelected={option => compareIds(option.id, value)}
      onChange={(_, value) => onChange?.(value as any)}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          size='small'
          variant='outlined'
          label='Select Team'
          {...textFieldProps}
        />
      )}
    />
  );
};

export default GroupSelector;
